import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getVideos, getChannels } from "../lib/api";
import { toast } from "sonner";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function Calendar() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [channelMap, setChannelMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterChannel, setFilterChannel] = useState("");

  const fetchData = async () => {
    try {
      const [videosRes, channelsRes] = await Promise.all([getVideos(), getChannels()]);
      setVideos(videosRes.data.filter(v => v.scheduled_date || v.published_date));
      setChannels(channelsRes.data);
      const cMap = {};
      channelsRes.data.forEach(c => { cMap[c.id] = c; });
      setChannelMap(cMap);
    } catch (err) {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getVideosForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return videos.filter(v => {
      const matchDate = v.scheduled_date === dateStr || v.published_date === dateStr;
      const matchChannel = !filterChannel || v.channel_id === filterChannel;
      return matchDate && matchChannel;
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleVideoClick = (video) => {
    if (video.channel_id) {
      navigate(`/channels/${video.channel_id}`);
    }
  };

  const getVideoColor = (video) => {
    if (video.stage === "published") return { bg: "bg-amber-500/20", border: "border-amber-500/40", text: "text-amber-300" };
    return { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-300" };
  };

  const days = getDaysInMonth(currentDate);
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <div className="skeleton h-[600px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigateMonth(-1)} variant="outline" className="btn-secondary p-2">
            <CaretLeft size={18} />
          </Button>
          <h2 className="font-heading text-xl font-semibold text-white min-w-[180px] text-center">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <Button onClick={() => navigateMonth(1)} variant="outline" className="btn-secondary p-2">
            <CaretRight size={18} />
          </Button>
          <Button onClick={() => setCurrentDate(new Date())} variant="outline" className="btn-secondary text-sm">
            Today
          </Button>
        </div>

        <Select value={filterChannel} onValueChange={(v) => setFilterChannel(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white" data-testid="calendar-filter-channel">
            <SelectValue placeholder="All Channels" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A2E] border-white/10">
            <SelectItem value="all" className="text-white">All Channels</SelectItem>
            {channels.map((c) => (
              <SelectItem key={c.id} value={c.id} className="text-white">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                  {c.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Calendar Grid */}
      <div className="surface-card overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-[#0B0C10]">
          {DAYS.map((day) => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-400 border-b border-white/5">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days.map((date, idx) => {
            const dateStr = date?.toISOString().split('T')[0];
            const dayVideos = getVideosForDate(date);
            const isToday = dateStr === today;

            return (
              <div
                key={`day-${dateStr || idx}`}
                className={`min-h-[130px] p-2 border-b border-r border-white/5 ${
                  !date ? "bg-[#0B0C10]/50" : ""
                } ${isToday ? "bg-blue-500/5 ring-1 ring-inset ring-blue-500/20" : ""}`}
                data-testid={`calendar-day-${dateStr || idx}`}
              >
                {date && (
                  <>
                    <div className={`text-sm mb-1.5 ${isToday ? "text-blue-400 font-bold" : "text-gray-300"}`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-1 overflow-y-auto scrollbar-thin" style={{ maxHeight: "calc(130px - 32px)" }}>
                      {dayVideos.map((video) => {
                        const colors = getVideoColor(video);
                        const channel = channelMap[video.channel_id];
                        return (
                          <button
                            key={video.id}
                            onClick={() => handleVideoClick(video)}
                            className={`w-full flex flex-col gap-0.5 px-2 py-1.5 rounded border text-left transition-all hover:brightness-125 cursor-pointer ${colors.bg} ${colors.border}`}
                            title={`${video.title}\nChannel: ${channel?.name || "Unknown"}\nStage: ${video.stage}`}
                            data-testid={`calendar-video-${video.id}`}
                          >
                            <span className={`text-xs font-medium truncate w-full ${colors.text}`}>
                              {video.title}
                            </span>
                            <span className="text-[10px] text-gray-400 truncate w-full">
                              {channel?.name || "Unknown Channel"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500/30 border border-red-500/50" />
          <span className="text-gray-400">Pending / Scheduled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-500/30 border border-amber-500/50" />
          <span className="text-gray-400">Published</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500/20 border border-blue-500" />
          <span className="text-gray-400">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <CalendarBlank size={14} className="text-gray-400" />
          <span className="text-gray-400">{videos.length} scheduled videos</span>
        </div>
      </div>
    </div>
  );
}
